
import { useState, useEffect } from 'react';
import {
  FaMapMarkerAlt,
  FaUsers,
  FaUmbrellaBeach,
  FaTree,
  FaHeart,
  FaStar,
  FaClock,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaWater,
  FaFish,
  FaUtensils,
  FaShip,
  FaSwimmingPool,
  FaDumbbell,
  FaSpa,
  FaPlane,
  FaShieldAlt,
  FaAmbulance,
  FaFireExtinguisher,
  FaInfoCircle,
  FaChevronRight,
  FaChevronLeft,
  FaCheckCircle,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaYoutube,
} from 'react-icons/fa';
import IslandMap2 from './IslandMap2';
import { FaLocationDot } from 'react-icons/fa6';
import { ScrollToTop } from './components';
import { RoundedImage } from './components/RoundedImage';
import Header2 from './components/Header2';
import HeroBackgroundSlider from './components/HeroBackgroundSlider';
import HeroBackgroundVideoSlider from './components/HeroBackgroundVideoSlider';

// ============================================================
// 1. DATA INTERFACES
// ============================================================
interface Location {
  lat: number;
  lng: number;
  name: string;
  description?: string;
}

interface EmergencyContact {
  service: string;
  number: string;
  icon?: string;
}

interface PopularPlace {
  name: string;
  description: string;
  image: string;
  rating: number;
  location: Location;
}

interface IslandDetail {
  label: string;
  value: string;
  icon: string;
}

interface Activity {
  label: string;
  icon: string;
  color: string;
}

interface Guideline {
  title: string;
  icon: string;
  items: string[];
}

interface IslandData {
  id: string;
  name: string;
  logoImage?: string;
  hero: {
    title: string;
    subtitle: string;
    logoImage?: string;
    backgroundImage: string;
    backgroundImages: string[];
    tag: string;
  };
  details: IslandDetail[];
  about: {
    title: string;
    description: string;
    stats: { label: string; value: string; icon: string }[];
    image: string;
    rating: number;
    reviews: number;
  };
  map: {
    center: Location;
    zoom: number;
  };
  popularPlaces: PopularPlace[];
  gallery: string[];
  activities: Activity[];
  emergencyContacts: EmergencyContact[];
  guidelines: Guideline[];
  contact: {
    phone: string;
    email: string;
    website: string;
    social: { platform: string; url: string; icon: string; color: string }[];
  };
  footer: {
    title: string;
    description: string;
    copyright: string;
  };
}

// ============================================================
// 2. ICON MAPPER
// ============================================================
const IconMap: Record<string, React.ReactNode> = {
  FaUsers: <FaUsers />,
  FaMapMarkerAlt: <FaMapMarkerAlt />,
  FaGlobe: <FaGlobe />,
  FaUmbrellaBeach: <FaUmbrellaBeach />,
  FaClock: <FaClock />,
  FaStar: <FaStar />,
  FaHeart: <FaHeart />,
  FaTree: <FaTree />,
  FaWater: <FaWater />,
  FaFish: <FaFish />,
  FaUtensils: <FaUtensils />,
  FaShip: <FaShip />,
  FaSwimmingPool: <FaSwimmingPool />,
  FaDumbbell: <FaDumbbell />,
  FaSpa: <FaSpa />,
  FaPlane: <FaPlane/>,
  FaShieldAlt: <FaShieldAlt />,
  FaAmbulance: <FaAmbulance />,
  FaFireExtinguisher: <FaFireExtinguisher />,
  FaPhone: <FaPhone />,
  FaEnvelope: <FaEnvelope />,
  FaFacebook: <FaFacebook />,
  FaInstagram: <FaInstagram />,
  FaTwitter: <FaTwitter />,
  FaYoutube: <FaYoutube />,
  FaLocationDot: <FaLocationDot />,
  FaInfoCircle: <FaInfoCircle />,
};

const getIcon = (iconName: string): React.ReactNode => {
  return IconMap[iconName] || <FaMapMarkerAlt />;
};

const getIconColor = (iconName: string): string => {
  const colorMap: Record<string, string> = {
    FaUsers: 'text-blue-500',
    FaMapMarkerAlt: 'text-green-500',
    FaGlobe: 'text-purple-500',
    FaUmbrellaBeach: 'text-yellow-500',
    FaClock: 'text-red-500',
    FaStar: 'text-orange-500',
    FaHeart: 'text-red-500',
    FaTree: 'text-green-500',
    FaWater: 'text-cyan-500',
    FaFish: 'text-indigo-500',
    FaUtensils: 'text-rose-500',
    FaShip: 'text-teal-500',
    FaSwimmingPool: 'text-sky-500',
    FaDumbbell: 'text-purple-500',
    FaSpa: 'text-emerald-500',
    FaPlane: 'text-emerald-500',
    FaShieldAlt: 'text-blue-600',
    FaAmbulance: 'text-red-600',
    FaFireExtinguisher: 'text-orange-600',
  };
  return colorMap[iconName] || 'text-gray-500';
};

// ============================================================
// 3. DEFAULT DATA (Maldives)
// ============================================================
const defaultIslandData: IslandData = {
  id: 'maldives',
  name: 'Ga. Villingili',
  hero: {
    title: 'Ga. Villingili',
    subtitle: 'Discover the beauty of Ga. Villingili — where turquoise waters meet pristine white sands',
    logoImage: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1600',
    backgroundImage: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1600',
    backgroundImages: ['https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1600', 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=1600'],
    tag: '🌴 Maldives',
  },
  details: [
    { label: 'Population', value: '5,200', icon: 'FaUsers' },
    { label: 'Area', value: '2.5 km²', icon: 'FaMapMarkerAlt' },
    { label: 'Language', value: 'Dhivehi, English', icon: 'FaGlobe' },
    { label: 'Currency', value: 'Maldivian Rufiyaa', icon: 'FaUmbrellaBeach' },
    { label: 'Time Zone', value: 'UTC +5', icon: 'FaClock' },
    { label: 'Best Season', value: 'Nov - April', icon: 'FaStar' },
  ],
  about: {
    title: 'Welcome to Paradise',
    description: 'Nestled in the heart of the Indian Ocean, the Maldives is a tropical paradise renowned for its stunning natural beauty, rich marine life, and warm hospitality. This island chain offers an unforgettable experience for travelers seeking tranquility, adventure, and cultural immersion.',
    stats: [
      { label: 'Visitor Satisfaction', value: '98%', icon: 'FaHeart' },
      { label: 'Atolls', value: '26', icon: 'FaUmbrellaBeach' },
      { label: 'Islands', value: '1190', icon: 'FaTree' },
      { label: 'Crystal Waters', value: '99%', icon: 'FaWater' },
    ],
    image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
    rating: 4.9,
    reviews: 1200,
  },
  map: {
    center: { lat: 0.751217, lng: 73.434759, name: 'Ga. Villingili Island Center' },
    zoom: 16,
  },
  popularPlaces: [
    {
      name: 'The Blue Bean Restaurant',
      description: 'A welcoming seaside restaurant known for its freshly prepared seafood, international cuisine, and relaxing atmosphere.',
      image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=600',
      rating: 4.9,
      location: { lat: 0.756065, lng: 73.438234, name: 'The Blue Bean Restaurant' },
    },
    {
      name: 'Ga. Atoll Education Center',
      description: 'A modern educational institution dedicated to academic excellence, innovation, and community development.',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600',
      rating: 4.8,
      location: { lat: 0.755703, lng: 73.434074, name: 'Ga. Atoll Education Center' },
    },
  ],
  gallery: [
    'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=800',
    'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800',
    'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?w=800',
    'https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?w=800',
    'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800',
    'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=800',
  ],
  activities: [
    { label: 'Snorkeling', icon: 'FaWater', color: 'from-cyan-400 to-blue-500' },
    { label: 'Diving', icon: 'FaFish', color: 'from-blue-400 to-indigo-500' },
    { label: 'Island Hopping', icon: 'FaShip', color: 'from-teal-400 to-cyan-500' },
    { label: 'Beach Relaxation', icon: 'FaUmbrellaBeach', color: 'from-yellow-400 to-orange-500' },
    { label: 'Fine Dining', icon: 'FaUtensils', color: 'from-rose-400 to-red-500' },
    { label: 'Poolside Lounge', icon: 'FaSwimmingPool', color: 'from-sky-400 to-blue-500' },
    { label: 'Wellness', icon: 'FaSpa', color: 'from-emerald-400 to-green-500' },
    { label: 'Fitness Center', icon: 'FaDumbbell', color: 'from-purple-400 to-pink-500' },
  ],
  emergencyContacts: [
    { service: 'Police', number: '119', icon: 'FaShieldAlt' },
    { service: 'Ambulance', number: '102', icon: 'FaAmbulance' },
    { service: 'Fire Service', number: '118', icon: 'FaFireExtinguisher' },
    { service: 'Coast Guard', number: '191', icon: 'FaShip' },
    { service: 'Tourist Police', number: '1414', icon: 'FaUmbrellaBeach' },
    { service: 'Hospital', number: '1400', icon: 'FaAmbulance' },
  ],
  guidelines: [
    {
      title: 'Entry Requirements',
      icon: '📋',
      items: [
        'Valid passport with 6 months validity',
        'Tourist visa on arrival (30 days)',
        'Return/onward ticket required',
        'Proof of accommodation booking',
      ],
    },
    {
      title: 'Important Tips',
      icon: '💡',
      items: [
        'Respect local culture and dress modestly',
        'Alcohol only allowed in resort islands',
        'Bargaining not common in shops',
        'Use reef-safe sunscreen to protect coral',
      ],
    },
    {
      title: 'Getting Around',
      icon: '🚤',
      items: [
        'Speed boats between islands',
        'Seaplanes for longer distances',
        'Ferry services available',
        'Bicycle rentals on some islands',
      ],
    },
    {
      title: 'Safety Guidelines',
      icon: '🌊',
      items: [
        'Swim only in designated areas',
        'Stay hydrated and use sun protection',
        'Follow local safety instructions',
        'Keep emergency numbers handy',
      ],
    },
  ],
  contact: {
    phone: '+960 123-4567',
    email: 'info@maldivesislands.com',
    website: 'www.maldivesislands.com',
    social: [
      { platform: 'Facebook', url: '#', icon: 'FaFacebook', color: 'bg-blue-600' },
      { platform: 'Instagram', url: '#', icon: 'FaInstagram', color: 'bg-pink-600' },
      { platform: 'Twitter', url: '#', icon: 'FaTwitter', color: 'bg-sky-500' },
      { platform: 'YouTube', url: '#', icon: 'FaYoutube', color: 'bg-red-600' },
    ],
  },
  footer: {
    title: 'Maldives Islands',
    description: 'Experience the ultimate island paradise with pristine beaches, crystal-clear waters, and unforgettable memories.',
    copyright: '© 2024 Maldives Islands. All rights reserved.',
  },
};

// ============================================================
// 4. MAIN COMPONENT
// ============================================================
interface IslandFrontProps {
  data?: IslandData;
}

export default function IslandView({ data = defaultIslandData }: IslandFrontProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<PopularPlace | null>(null);

  const {
    hero,
    details,
    about,
    map,
    popularPlaces,
    gallery,
    activities,
    emergencyContacts,
    guidelines,
    contact,
    footer
  } = data;

  // Auto-slide for gallery
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % gallery.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [gallery.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % gallery.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + gallery.length) % gallery.length);
  };

  const heroMedia = [
  // {
  //   type: "video",
  //   src: "https://storage.villingiliplus.com/Ga_Villingili/images/24498971-e34e-4994-a593-173159e7df26.mp4",
  // },
  // {
  //   type: "video",
  //   src: "https://storage.villingiliplus.com/Ga_Villingili/images/53ed8592-dda8-4c43-adf3-7db190d572a1.mp4",
  // },
  {
    type: "video",
    src: "https://storage.villingiliplus.com/Ga_Villingili/images/2800b69f-aec1-420a-8c37-2e540e00585d.mp4",
  },
  // {
  //   type: "image",
  //   src: "https://storage.villingiliplus.com/Ga_Villingili/images/1419ab67-34d9-452c-92a9-1e925a16cb86.jpeg",
  // },
];

const isVideo = (url: string) => {
  return /\.(mp4|webm|ogg|mov)$/i.test(url);
};

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <ScrollToTop />

      {/* ============================================================
          HERO SECTION
      ============================================================ */}
      <section

  id="home"

  className="relative h-[600px] flex items-center justify-center overflow-hidden"

>

  {/* <HeroBackgroundSlider
    images={hero.backgroundImages}
    interval={5000}
  /> */}

  <HeroBackgroundVideoSlider
    items={heroMedia}
    interval={8000}
    showIndicators
  />

  <Header2
    siteName="GA. VILLINGILI"
    logo={<RoundedImage imageUrl={hero.backgroundImage} />}
    links={[
      { label: "Home", target: "home" },
      { label: "Details", target: "islanddetails" },
      { label: "Discover", target: "discover" },
      { label: "Places", target: "popularplaces" },
      { label: "Contact", target: "contact" },
    ]}
  />

        <div className="relative z-10 text-center px-4 max-w-4xl">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium tracking-wider">
              {hero.tag}
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-primary font-bold text-white mb-6">
            {hero.title}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 font-secondary leading-relaxed mb-8">
            {hero.subtitle}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button className="px-8 py-3  bg-blue-600/20 backdrop-blur-md hover:bg-blue/30 hover:from-blue-700 hover:to-blue-800 text-white rounded-full font-primary font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-white/30">
              Explore Now
            </button>
            <button className="px-8 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white rounded-full font-primary font-semibold transition-all duration-300 border border-white/30">
              We serve our guests
            </button>

            {/* <RoundedImage imageUrl={hero.backgroundImage} /> */}
{/* 
            <div className="w-20 h-20 rounded-full p-1 bg-amber-200 animate-pulse">
              <img className='w-full h-full rounded-full' src={hero.backgroundImage} alt="bg" />
            </div> */}
          
          </div>
        </div>
      </section>

      {/* ============================================================
          ISLAND DETAILS SECTION
      ============================================================ */}
      <section id='islanddetails' className="container mx-auto max-w-7xl px-4 -mt-16 relative z-20">
        <div className="bg-white rounded-3xl shadow-2xl p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {details.map((detail, index) => (
            <div key={index} className="flex items-center gap-4 p-4 rounded-xl hover:bg-blue-50/50 transition-colors duration-200">
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center text-2xl ${getIconColor(detail.icon)}`}>
                {getIcon(detail.icon)}
              </div>
              <div>
                <p className="font-secondary text-xs text-gray-500 uppercase tracking-wider">
                  {detail.label}
                </p>
                <p className="font-primary font-semibold text-gray-800">
                  {detail.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

    
      {/* ============================================================
          ABOUT SECTION
      ============================================================ */}
      <section id='about' className="container mx-auto max-w-7xl px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              About the Island
            </span>
            <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
              {about.title}
            </h2>
            <p className="font-secondary text-gray-600 leading-relaxed mb-6">
              {about.description}
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {about.stats.map((stat, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className={`text-xl ${getIconColor(stat.icon)}`}>
                    {getIcon(stat.icon)}
                  </span>
                  <span className="font-secondary text-sm text-gray-700">
                    {stat.value} {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src={about.image}
              alt="Island"
              className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 rounded-full p-3">
                  <FaStar className="text-yellow-500 text-2xl" />
                </div>
                <div>
                  <p className="font-primary font-bold text-2xl text-gray-800">{about.rating}</p>
                  <p className="font-secondary text-sm text-gray-500">{about.reviews}+ reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          POPULAR PLACES
      ============================================================ */}
      <section id='popularplaces' className="bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Explore
            </span>
            <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Popular Places
            </h2>
            <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
              Discover the most enchanting destinations in the island
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularPlaces.map((place, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative overflow-hidden h-48">
                  <img
                    src={place.image}
                    alt={place.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                    <FaStar className="text-yellow-400 text-xs" />
                    <span className="font-secondary text-xs font-semibold text-gray-700">{place.rating}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-primary font-bold text-lg text-gray-800 mb-2">
                    {place.name}
                  </h3>
                  <p className="font-secondary text-sm text-gray-500 leading-relaxed line-clamp-2">
                    {place.description}
                  </p>
                </div>



                {/* <GoogleMapEmbed
  center={map.center}
  locations={popularPlaces.map(place => ({
    ...place.location,
    name: place.name,
    description: place.description,
  }))}
  zoom={map.zoom}
  height="500px"
  className="shadow-2xl"
  onLocationClick={(location) => {
    const place = popularPlaces.find(p => 
      p.location.lat === location.lat && 
      p.location.lng === location.lng
    );
    if (place) {
      setSelectedLocation(place);
    }
  }}
/> */}


              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          PHOTO GALLERY
      ============================================================ */}
      <section className="container mx-auto max-w-7xl px-4 py-20">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
            Gallery
          </span>
          <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Island Moments
          </h2>
          <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
            A glimpse into the breathtaking beauty of the island
          </p>
        </div>

        <div className="relative">
          <div className="overflow-hidden rounded-3xl shadow-2xl">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {gallery.map((media, index) => (

    <div

      key={index}

      className="w-full flex-shrink-0"

    >

      {isVideo(media) ? (

        <video

          className="w-full h-[500px] object-cover"

          controls

          playsInline

          preload="metadata"

        >

          <source src={media} />

          Your browser does not support video playback.

        </video>

      ) : (

        <img

          src={media}

          alt={`Gallery ${index + 1}`}

          className="w-full h-[500px] object-cover cursor-pointer"

          onClick={() => setSelectedImage(media)}

        />

      )}

    </div>

  ))}
            </div>
          </div>

          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
          >
            <FaChevronLeft className="text-gray-800" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-sm hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
          >
            <FaChevronRight className="text-gray-800" />
          </button>

          <div className="flex justify-center gap-2 mt-6">
            {gallery.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          ACTIVITIES & AMENITIES
      ============================================================ */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Activities
            </span>
            <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Things to Do
            </h2>
            <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
              Experience the best of island life with our curated activities
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {activities.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${item.color} mx-auto flex items-center justify-center text-white text-3xl group-hover:scale-110 transition-transform duration-300`}>
                  {getIcon(item.icon)}
                </div>
                <p className="font-primary font-semibold text-gray-800 mt-4">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

        {/* ============================================================
          MAP SECTION
      ============================================================ */}
      <section id='discover' className="container mx-auto max-w-7xl px-4 py-20">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
            Explore
          </span>
          <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Island Map
          </h2>
          <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
            Explore the island and discover popular locations, attractions, and hidden gems
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 ">
            <IslandMap2
              center={map.center}
              locations={popularPlaces.map(place => ({
                ...place.location,
                name: place.name,
                description: place.description,
              }))}
              zoom={map.zoom}
              height="500px"
              className="shadow-2xl"
              selectedLocation={selectedLocation?.location || null} // Pass selected location
              onLocationClick={(location) => {
                const place = popularPlaces.find(p =>
                  p.location.lat === location.lat &&
                  p.location.lng === location.lng
                );
                if (place) {
                  setSelectedLocation(place);
                } else {
                  // If clicking center, deselect
                  setSelectedLocation(null);
                }
              }}
            />

          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24">
              <h3 className="font-primary text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaLocationDot className="text-red-500" />
                Popular Locations
              </h3>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {popularPlaces.map((place, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedLocation(place);
                      document.querySelector('.map-container')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`w-full text-left p-3 rounded-xl transition-all duration-200 ${selectedLocation?.name === place.name
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-blue-50/50 border-2 border-transparent'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                        {String.fromCharCode(65 + index)}
                      </div>
                      <div className="flex-1">
                        <p className="font-primary font-semibold text-sm text-gray-800">
                          {place.name}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <FaStar className="text-yellow-400 text-xs" />
                          <span className="font-secondary text-xs text-gray-500">{place.rating}</span>
                        </div>
                      </div>
                      <FaChevronRight className="text-gray-400 text-xs" />
                    </div>
                  </button>
                ))}
              </div>

              {selectedLocation && (
                <div id="location-info" className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <h4 className="font-primary font-semibold text-gray-800 mb-1">
                    {selectedLocation.name}
                  </h4>
                  <p className="font-secondary text-sm text-gray-600 leading-relaxed">
                    {selectedLocation.description}
                  </p>
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <FaMapMarkerAlt className="text-red-500" />
                    <span>
                      {selectedLocation.location.lat.toFixed(4)}°N, {selectedLocation.location.lng.toFixed(4)}°E
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>


      {/* ============================================================
          EMERGENCY CONTACTS
      ============================================================ */}
      <section className="container mx-auto max-w-7xl px-4 py-20">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-4">
            Safety First
          </span>
          <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Emergency Contacts
          </h2>
          <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
            Important numbers to keep handy during your stay
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {emergencyContacts.map((contact, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-red-200"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center text-3xl">
                  {getIcon(contact.icon || 'FaShieldAlt')}
                </div>
                <div>
                  <p className="font-primary font-semibold text-gray-800">
                    {contact.service}
                  </p>
                  <a
                    href={`tel:${contact.number}`}
                    className="font-primary font-bold text-2xl text-blue-600 hover:text-blue-700 transition-colors duration-200"
                  >
                    {contact.number}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <div className="flex items-center justify-center gap-2 text-red-600">
            <FaInfoCircle className="text-xl" />
            <span className="font-secondary font-medium">
              For immediate assistance, dial 119 (Police) or 102 (Ambulance) from any local phone
            </span>
          </div>
        </div>
      </section>

      {/* ============================================================
          TRAVEL GUIDELINES
      ============================================================ */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Plan Your Trip
            </span>
            <h2 className="font-primary text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Travel Guidelines
            </h2>
            <p className="font-secondary text-gray-500 max-w-2xl mx-auto">
              Everything you need to know before visiting
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {guidelines.map((guideline, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-2xl">
                    {guideline.icon}
                  </div>
                  <h3 className="font-primary text-xl font-bold text-gray-800">{guideline.title}</h3>
                </div>
                <ul className="space-y-3">
                  {guideline.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <FaCheckCircle className="text-blue-500 mt-1" />
                      <span className="font-secondary text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FOOTER
      ============================================================ */}
      <section className="bg-gray-900 text-white py-16">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-primary text-2xl font-bold mb-4">{footer.title}</h3>
              <p className="font-secondary text-gray-400 leading-relaxed">
                {footer.description}
              </p>
            </div>
            <div>
              <h4 className="font-primary font-bold text-lg mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-400">
                  <FaPhone className="text-blue-400" />
                  <span>{contact.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <FaEnvelope className="text-blue-400" />
                  <span>{contact.email}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <FaGlobe className="text-blue-400" />
                  <span>{contact.website}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-primary font-bold text-lg mb-4">Follow Us</h4>
              <div className="flex gap-4">
                {contact.social.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className={`w-12 h-12 rounded-full ${social.color} hover:opacity-80 transition-colors duration-200 flex items-center justify-center text-xl`}
                  >
                    {getIcon(social.icon)}
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 font-secondary text-sm">
            <p>{footer.copyright}</p>
          </div>
        </div>
      </section>

      {/* ============================================================
          LIGHTBOX MODAL
      ============================================================ */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-lg flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white/70 hover:text-white text-3xl transition-colors duration-200"
          >
            ✕
          </button>
          <img
            src={selectedImage}
            alt="Full size view"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// 5. EXPORT DEFAULT DATA FOR REUSE
// ============================================================
export { defaultIslandData, type IslandData, type PopularPlace, type Location };